import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { MusicVideoSettings } from 'fym-shared/src/music24/musicVideoDoc';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { panelMessages } from '../messages/panelMessages';
import { Segmented } from '../panels/Segmented';
import { formatTime } from '../timeline/timelineGeometry';
import { effectMessages } from '../messages/effectMessages';
import { CutsList } from './CutsList';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The inspector with nothing selected (design d4): the sound (the master, locked), the
 * start and the end, the fades, how much footage is bank and own, the count of the effects (the
 * "+ Add effect" menu is in the timeline bar, design directed-video h3), and the cuts of the song.
 */
const VideoSettingsComponent: React.FC = () => {
  const intl = useIntl();
  const { doc, data, change, readOnly, cutUuid } = useMusicVideoEditor();
  const { settings } = doc;
  const patch = (next: Partial<MusicVideoSettings>) =>
    change((current) => ({ ...current, settings: { ...current.settings, ...next } }));
  const counts = useMemo(() => {
    const own = doc.clips.filter((clip) => clip.source.kind === 'footage').length;
    return { own, bank: doc.clips.length - own, total: Math.max(1, doc.clips.length) };
  }, [doc.clips]);
  const cutNumber = data.cuts.find((cut) => cut.uuid === cutUuid)?.number ?? 1;
  const songEnd = data.song.durationSec ?? settings.endSec;

  return (
    <>
      <h4 className={styles.inspectorHead}>
        {intl.formatMessage(panelMessages.videoHeading)}
        <span className={styles.inspectorSub}>
          {intl.formatMessage(panelMessages.videoSub, { title: data.song.title, number: cutNumber })}
        </span>
      </h4>
      <div className={styles.inspectorGrid}>
        <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.sound)}</span>
        <span className={`${styles.field} ${styles.fieldMono}`}>
          {intl.formatMessage(panelMessages.soundValue)}
          <span className={styles.fieldUnit}>{intl.formatMessage(panelMessages.locked)}</span>
        </span>
        <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.videoStarts)}</span>
        <span className={`${styles.field} ${styles.fieldMono}`}>
          {formatTime(settings.startSec, true)}
          {settings.startSec === 0 && (
            <span className={styles.fieldUnit}>{intl.formatMessage(panelMessages.songStart)}</span>
          )}
        </span>
        <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.videoEnds)}</span>
        <span className={`${styles.field} ${styles.fieldMono}`}>
          {formatTime(settings.endSec, true)}
          {Math.abs(settings.endSec - songEnd) < 0.01 && (
            <span className={styles.fieldUnit}>{intl.formatMessage(panelMessages.songEnd)}</span>
          )}
        </span>
        <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.fadeIn)}</span>
        <Segmented<'off' | 'on'>
          label={intl.formatMessage(panelMessages.fadeIn)}
          value={settings.fadeIn ? 'on' : 'off'}
          disabled={readOnly}
          options={[
            { value: 'off', label: intl.formatMessage(panelMessages.off) },
            { value: 'on', label: intl.formatMessage(panelMessages.fromBlack) },
          ]}
          onChange={(value) => patch({ fadeIn: value === 'on' })}
        />
        <span className={styles.rowLabel}>{intl.formatMessage(panelMessages.fadeOut)}</span>
        <Segmented<'off' | 'on'>
          label={intl.formatMessage(panelMessages.fadeOut)}
          value={settings.fadeOut ? 'on' : 'off'}
          disabled={readOnly}
          options={[
            { value: 'off', label: intl.formatMessage(panelMessages.off) },
            { value: 'on', label: intl.formatMessage(panelMessages.toBlack) },
          ]}
          onChange={(value) => patch({ fadeOut: value === 'on' })}
        />
      </div>
      <div className={styles.separator} />
      <span className={styles.eyebrow}>{intl.formatMessage(panelMessages.footageEyebrow)}</span>
      {[
        { label: panelMessages.bankCount, count: counts.bank },
        { label: panelMessages.ownCount, count: counts.own },
      ].map((row) => (
        <div key={row.label.id} className={styles.meterBox}>
          <div className={styles.meterLine}>
            <span>{intl.formatMessage(row.label)}</span>
            <span>{intl.formatMessage(panelMessages.countClips, { count: row.count })}</span>
          </div>
          <div className={styles.meter}>
            <span style={{ width: `${(row.count / counts.total) * 100}%` }} />
          </div>
        </div>
      ))}
      <div className={styles.separator} />
      <div className={styles.column}>
        <span className={styles.eyebrow}>{intl.formatMessage(effectMessages.addTitle)}</span>
        <span className={styles.rowLabel}>
          {intl.formatMessage(effectMessages.addCount, { count: doc.effects.length })}
        </span>
        <p className={styles.hint}>{intl.formatMessage(effectMessages.addFromBar)}</p>
      </div>
      <div className={styles.separator} />
      <CutsList />
    </>
  );
};

export const VideoSettings = TypedMemo(VideoSettingsComponent);
